'use client';

import { useMemo } from 'react';
import type { MarketplaceComposition, MarketplaceSellable } from '@/types/marketplace';
import { ProductCardProps } from '@/components/ProductCard/ProductCard';
import ProductSlider, { ProductSliderProps } from '@/components/ProductSlider/ProductSlider';
import { tracker } from '@/utility/tracking/tracker';
import { Feature, SearchData } from '@/utility/tracking/types';

export type ProductCardWithTracking = ProductCardProps & {
  compositionId: MarketplaceComposition['id'];
  sellableId: MarketplaceSellable['id'];
  // Needed for the custom sticker recommendation item
  disableTracking?: boolean;
};

type SimilarDesignsSliderProps = Omit<ProductSliderProps, 'items'> & {
  items: ProductCardWithTracking[];
  tracking: Feature<
    'product-card-click',
    | 'reco also-like'
    | 'reco similar-designs'
    | 'reco purchased-together'
    | 'reco most-purchased'
    | 'reco sticker'
    | 'reco recently-viewed'
    | `reco this-design-on-other-products ${string}`,
    { search: SearchData }
  >;
};

const SimilarDesignsSlider = ({ tracking, items, ...rest }: SimilarDesignsSliderProps) => {
  const itemsWithTracking = useMemo(() => {
    const trackableItems = items.filter((item) => !item.disableTracking);

    return items.map(
      (item: ProductCardWithTracking) =>
        ({
          ...item,
          link: {
            ...item.link,
            onClick: (_, appearanceId: string) => {
              // We do not send the responseId and position if tracking is disabled for this item
              const disableTracking = item.disableTracking ?? false;
              const pageDataDetails = disableTracking ? undefined : tracking.pageDataDetails;
              const position = disableTracking ? undefined : trackableItems.indexOf(item) + 1; // 1-based index

              tracker.trackFeature({
                ...tracking,
                pageDataDetails,
                product: {
                  productTypeId: item.productTypeId,
                  appearanceId,
                  price: item.price,
                  currentDiscountAmount: item.currentDiscountAmount,
                  amount: 1,
                  articleId: item.compositionId,
                  articleName: item.subTitle,
                  sellableId: item.sellableId,
                  position,
                },
              });
            },
          },
        }) satisfies ProductCardProps
    );
  }, [items, tracking]);

  return (
    <ProductSlider
      {...rest}
      items={itemsWithTracking}
      optimizeImages={true}
      deferAppearances={true}
      deferBadges={true}
    />
  );
};

export default SimilarDesignsSlider;
